import {
  TextInput as ReactNativeTextInput,
  View,
  TextStyle,
  TextInputProps,
  ViewStyle,
  NativeSyntheticEvent,
  TextInputFocusEventData,
  Image,
  ImageStyle,
  Pressable,
} from 'react-native';
import React, {useState, useEffect, useCallback} from 'react';
import {corner_radius, spacing} from 'theme/Units';
import colors from 'theme/Colors';
import {mergeDeepRight} from 'ramda';
import {Text} from 'ui-kits';
import {nonDigitRegex} from 'constants/regex';

type sizeTypes = 'lg' | 'md' | 'sm';
type inputStateTypes = 'default' | 'disabled' | 'error' | 'focus';
type inputTypes = 'text' | 'password' | 'numeric';

type propType = Readonly<{
  label: string;
  helperLabel?: string;
  optionalLabel?: string;
  placeholder?: string;
  size?: sizeTypes;
  type?: inputTypes;
  disabled?: boolean;
  error?: string;
  required?: boolean;
  width?: number;
  leftIcon?: JSX.Element;
  rightIcon?: JSX.Element;
}> &
  TextInputProps;

export const TextInput = (props: propType): JSX.Element => {
  const {
    label,
    placeholder,
    helperLabel,
    optionalLabel,
    size = 'md',
    type = 'text',
    error,
    disabled,
    leftIcon = null,
    rightIcon = null,
    onFocus,
    onBlur,
    required = false,
    width,
    onChangeText,
    id,
    ...rest
  } = props;

  const [inputState, setInputState] = useState<inputStateTypes>(
    disabled ? 'disabled' : 'default',
  );
  const [secureText, setSecureText] = useState<boolean>(type === 'password');

  const handleTextChange = (text: string) => {
    if (type === 'numeric') {
      const numericValue = text.replace(nonDigitRegex, ''); // Remove non-numeric characters

      onChangeText?.(numericValue);
    } else {
      onChangeText?.(text);
    }
  };

  /**
   * overriding styles based on size variants as well as input states
   */
  const styles = mergeDeepRight(
    mergeDeepRight(baseStyles, inputStateStyle[inputState]),
    sizeStyles[size],
  ) as styleProps;

  const updateInputState = useCallback(
    (state: inputStateTypes) => {
      if (disabled) {
        return;
      }
      setInputState(state);
    },
    [disabled],
  );

  // text input event handlers
  function onFocusHandler(
    e: NativeSyntheticEvent<TextInputFocusEventData>,
  ): void {
    updateInputState('focus');
    onFocus?.(e); // calling the onFocus handler passed as prop
  }

  function onBlurHanlder(
    e: NativeSyntheticEvent<TextInputFocusEventData>,
  ): void {
    updateInputState(error ? 'error' : 'default');
    onBlur?.(e); // calling the onBlur handler passed as prop
  }

  // default icons based on the type <password, text, etc> of the Input Field
  const iconVariants: Partial<
    Record<
      inputTypes,
      {
        leftIcon?: JSX.Element | null;
        rightIcon?: JSX.Element | null;
      }
    >
  > = {
    // password input field will have a default left icon for toggling password shown
    password: {
      rightIcon: (
        <Pressable onPress={() => setSecureText(!secureText)}>
          {/* updating local state to show/hide password */}
          {secureText ? (
            <Image
              style={styles.inputIcon}
              source={require('assets/icons/eye_off@2x.png')}
            />
          ) : (
            <Image
              style={styles.inputIcon}
              source={require('assets/icons/eye@2x.png')}
            />
          )}
        </Pressable>
      ),
    },
  };

  useEffect(() => {
    if (error) {
      updateInputState('error');
    } else {
      updateInputState('default');
    }
  }, [error, updateInputState]);

  const right_icon = rightIcon || iconVariants[type]?.rightIcon;
  const left_icon = leftIcon || iconVariants[type]?.leftIcon;

  return (
    <View id={id} style={[styles.inputContainer, {maxWidth: width}]}>
      {/* label and optional text */}
      <View style={styles.labelWrapper}>
        <Text style={styles.label}>
          {label} {required ? <Text style={styles.redAsterisk}>*</Text> : null}
        </Text>
        <Text style={styles.optionnalLabel}>{optionalLabel}</Text>
      </View>

      {/* input wrapper that renders right, left icon and input field */}
      <View style={styles.inputWrapper}>
        {/* left icon */}
        {left_icon}

        {/* input */}
        <ReactNativeTextInput
          onFocus={onFocusHandler}
          onBlur={onBlurHanlder}
          placeholderTextColor={colors.palette.nuetral4}
          style={styles.inputFeild}
          placeholder={placeholder}
          secureTextEntry={secureText}
          onChangeText={handleTextChange}
          disabled={disabled}
          {...rest}
          // these two attribute are for disabling auto aggressive password filling.
          // We need this since in password input fields on firefox browser,
          // it agressively fills out the password value from its cached values
          // which overrides Formik initalvalues, hence it result in unexpected behavoirs.
          // in forms.
          textContentType="newPassword"
          autoComplete="new-password"
        />
        {/* right icon */}
        {right_icon}
      </View>

      {/* helper text and error label */}
      {helperLabel ? (
        <Text style={styles.helperLabel}>{helperLabel}</Text>
      ) : null}
      {error ? <Text style={styles.errorLabel}>{error}</Text> : null}
    </View>
  );
};

type styleProps = {
  label: TextStyle;
  helperLabel: TextStyle;
  optionnalLabel: TextStyle;
  errorLabel: TextStyle;
  inputFeild: TextStyle;
  inputContainer: ViewStyle;
  inputIcon: ImageStyle;
  inputWrapper: ViewStyle;
  redAsterisk: TextStyle;
  labelWrapper: ViewStyle;
};

const baseStyles: styleProps = {
  inputContainer: {
    width: '100%',
    gap: spacing['1'],
    minWidth: 0,
  },
  redAsterisk: {color: colors.palette.errorRedDark},
  label: {
    fontWeight: '600',
    fontSize: 16,
    color: colors.palette.black,
  },
  optionnalLabel: {
    color: colors.palette.secondaryBlack5,
    fontSize: 12,
    fontWeight: '500',
  },
  helperLabel: {
    color: colors.palette.secondaryBlack5,
    fontSize: 12,
    fontWeight: '400',
  },
  inputWrapper: {
    borderWidth: 2,
    borderStyle: 'solid',
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing['2'],
    paddingVertical: spacing['1'],
    borderRadius: corner_radius.rounded,
    paddingHorizontal: spacing['2'],
    backgroundColor: colors.palette.white,
  },
  labelWrapper: {flexDirection: 'row', justifyContent: 'space-between'},
  inputFeild: {
    height: '100%',
    flex: 1,
    minWidth: 0,
    fontWeight: '500',
    color: colors.palette.black,
    backgroundColor: 'transparent',
    outlineColor: 'transparent',
  },
  inputIcon: {
    width: 14,
    height: 14,
    resizeMode: 'contain',
  },
  errorLabel: {
    color: colors.palette.errorRedDark,
    fontSize: 12,
    fontWeight: '400',
  },
};

const inputStateStyle: Record<inputStateTypes, Partial<styleProps>> = {
  error: {
    label: {
      color: colors.palette.errorRedDark,
    },
    inputWrapper: {
      borderColor: colors.palette.errorRedDark,
    },
  },
  disabled: {
    inputWrapper: {
      borderColor: colors.palette.stroke,
      backgroundColor: 'rgba(160, 160, 160, 0.2)',
    },
  },
  focus: {
    inputWrapper: {
      borderWidth: 3,
      borderColor: 'rgba(158, 119, 237, 0.4)',
    },
  },
  default: {
    inputWrapper: {
      borderColor: colors.palette.stroke,
    },
  },
};

const sizeStyles: Record<sizeTypes, Partial<styleProps>> = {
  lg: {
    inputWrapper: {
      height: 48,
    },
    inputFeild: {
      fontSize: 18,
    },
    label: {
      fontSize: 18,
    },
  },
  sm: {
    inputWrapper: {
      height: 32,
    },
    inputFeild: {
      fontSize: 12,
    },
    label: {
      fontSize: 14,
    },
  },
  md: {
    inputWrapper: {
      height: 40,
    },
    inputFeild: {
      fontSize: 14,
    },
    label: {
      fontSize: 16,
    },
  },
};
